import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { TslNewsletterService } from "services/api-workers-service"
import type {
  TslNewsletterPreviewJson,
  TslNewsletterPreviewLanguage,
  TslNewsletterPreviewMode,
  TslNewsletterPreviewQuery,
} from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import CheckboxesGroup from "ui/components/atoms/checkboxes-group/CheckboxesGroup"
import Input from "ui/components/atoms/input"
import RadioSlider from "ui/components/atoms/radio-slider/RadioSlider"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import { Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"

import { TSL_LANGUAGES, TSL_POSITIONS, TSL_TAGS } from "./data"
import TslNewsletterNav from "./TslNewsletterNav"

const MODE_OPTIONS: string[][] = [
  ["Dzienny", "daily"],
  ["Tygodniowy", "weekly"],
]

const DEBOUNCE_MS = 500

const buildQuery = (
  language: TslNewsletterPreviewLanguage,
  position: string,
  tags: string[],
  mode: TslNewsletterPreviewMode,
  firstName: string,
): TslNewsletterPreviewQuery => ({
  language,
  position,
  tags: tags.join(","),
  mode,
  first_name: firstName || undefined,
})

const errorDetail = (e: unknown): string => {
  const err = e as { response?: { status?: number; data?: { detail?: unknown } } }
  const status = err?.response?.status
  const detail = err?.response?.data?.detail
  if (status === 401) return "Brak autoryzacji — sprawdź klucz API."
  if (status === 422) {
    if (typeof detail === "string") return detail
    if (Array.isArray(detail)) {
      return detail.map(d => (typeof d === "string" ? d : JSON.stringify(d))).join("; ")
    }
    return "Nieprawidłowe parametry zapytania."
  }
  if (typeof detail === "string") return detail
  return "Nie udało się pobrać podglądu newslettera."
}

const Preview = () => {
  const [language, setLanguage] = useState<TslNewsletterPreviewLanguage>("pl")
  const [position, setPosition] = useState<string>(TSL_POSITIONS[0][1])
  const [tags, setTags] = useState<string[]>([])
  const [mode, setMode] = useState<TslNewsletterPreviewMode>("daily")
  const [firstName, setFirstName] = useState<string>("Podgląd")
  const [showJson, setShowJson] = useState<boolean>(false)

  const [debounced, setDebounced] = useState<TslNewsletterPreviewQuery>(() =>
    buildQuery(language, position, tags, mode, firstName),
  )
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebounced(buildQuery(language, position, tags, mode, firstName))
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [language, position, tags, mode, firstName])

  const queryKey = useMemo(() => ["tsl", "preview", debounced] as const, [debounced])

  const {
    data: html,
    isFetching: isFetchingHtml,
    isError: isErrorHtml,
    error: errorHtml,
    refetch: refetchHtml,
  } = useQuery({
    queryKey: [...queryKey, "html"],
    queryFn: () => TslNewsletterService.previewNewsletterHtml(debounced),
    enabled: !!debounced.position,
    retry: false,
  })

  const {
    data: json,
    isFetching: isFetchingJson,
    isError: isErrorJson,
    error: errorJson,
    refetch: refetchJson,
  } = useQuery<TslNewsletterPreviewJson>({
    queryKey: [...queryKey, "json"],
    queryFn: () => TslNewsletterService.previewNewsletterJson(debounced),
    enabled: showJson && !!debounced.position,
    retry: false,
  })

  useEffect(() => {
    if (isErrorHtml) SentryService.error("[ERROR TslNewsletter.previewHtml]:", errorHtml)
    if (isErrorJson) SentryService.error("[ERROR TslNewsletter.previewJson]:", errorJson)
  }, [isErrorHtml, isErrorJson, errorHtml, errorJson])

  const hasContent = json ? json.has_content : !!html && html.length > 0
  const isLoading = isFetchingHtml || (showJson && isFetchingJson)

  return (
    <PanelLayoutContent
      title="TSL Newsletter"
      text="Podgląd newslettera dla wybranej konfiguracji subskrybenta"
    >
      <TslNewsletterNav />

      <FiltersWrapper $marginBottom="M">
        <Select
          name="language"
          label="Język"
          options={TSL_LANGUAGES}
          value={language}
          onChange={({ value }) => setLanguage(value as TslNewsletterPreviewLanguage)}
          $minWidth="200px"
        />
        <Select
          name="position"
          label="Stanowisko"
          options={TSL_POSITIONS}
          value={position}
          onChange={({ value }) => setPosition(value)}
          $minWidth="260px"
        />
        <Box $minWidth="220px">
          <Label $type="secondary" $marginBottom="XS">
            Tryb
          </Label>
          <RadioSlider
            name="mode"
            options={MODE_OPTIONS}
            value={mode}
            onChange={({ value }) => setMode(value as TslNewsletterPreviewMode)}
          />
        </Box>
        <Input
          name="first_name"
          label="Imię (greeting)"
          placeholder="Podgląd"
          value={firstName}
          onChange={({ value }) => setFirstName(value)}
          $width="220px"
        />
      </FiltersWrapper>

      <Box $marginBottom="M">
        <CheckboxesGroup
          name="tags"
          label="Tagi (puste = brak filtra)"
          $type="secondary"
          options={TSL_TAGS}
          value={tags}
          onChange={({ value }) => setTags(value as string[])}
        />
      </Box>

      <Row $alignItems="center" $marginBottom="S" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button
          icon="boost"
          onClick={() => {
            refetchHtml()
            if (showJson) refetchJson()
          }}
        >
          Odśwież
        </Button>
        <Button
          $type="secondary"
          icon={showJson ? "minus" : "plus"}
          onClick={() => setShowJson(s => !s)}
        >
          {showJson ? "Ukryj dane (JSON)" : "Pokaż dane (JSON)"}
        </Button>
        {isLoading && (
          <Row $alignItems="center" style={{ gap: 8 }}>
            <Spinner $size="18px" />
            <P $small $marginBottom="0">
              Generuję podgląd…
            </P>
          </Row>
        )}
      </Row>

      {isErrorHtml && (
        <Alert $type="error" $marginBottom="S">
          {errorDetail(errorHtml)}
        </Alert>
      )}

      {!isErrorHtml && !isFetchingHtml && !hasContent && (
        <EmptyState>
          <P $marginBottom="0">Brak treści dla tej konfiguracji.</P>
        </EmptyState>
      )}

      {!isErrorHtml && (
        <PreviewFrameWrapper>
          {isFetchingHtml && !html ? (
            <Row $justifyContent="center" $padding="M">
              <Spinner $size="30px" />
            </Row>
          ) : html ? (
            <PreviewFrame
              title="TSL newsletter preview"
              sandbox="allow-same-origin"
              srcDoc={html}
            />
          ) : null}
        </PreviewFrameWrapper>
      )}

      {showJson && (
        <DebugWrapper $marginTop="M">
          <Label $type="secondary" $marginBottom="XS">
            Dane (JSON)
          </Label>
          {isErrorJson ? (
            <Alert $type="error">{errorDetail(errorJson)}</Alert>
          ) : isFetchingJson && !json ? (
            <Row $justifyContent="center" $padding="M">
              <Spinner $size="24px" />
            </Row>
          ) : json ? (
            <Pre>{JSON.stringify(json, null, 2)}</Pre>
          ) : null}
        </DebugWrapper>
      )}
    </PanelLayoutContent>
  )
}

export default Preview

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.S};
`

const PreviewFrameWrapper = styled(Box)`
  background: white;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  overflow: hidden;
`

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 800px;
  border: 0;
  display: block;
  background: white;
`

const EmptyState = styled(Box)`
  padding: ${({ theme }) => theme.space.M};
  text-align: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.M};
`

const DebugWrapper = styled(Box)`
  background: white;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  padding: ${({ theme }) => theme.space.S};
`

const Pre = styled.pre`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  max-height: 480px;
  overflow: auto;
  white-space: pre;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`
